export type MeasurementSystem = "METRIC" | "IMPERIAL";
export type MeasurementKind = "LENGTH" | "MASS" | "VOLUME";

export interface MeasurementConfiguration {
  system: MeasurementSystem;
  millimetersPerInch: number;
  kilogramsPerPound: number;
  cubicMillimetersPerCubicInch: number;
  displayDecimals: number;
}

export const STANDARD_MEASUREMENT_CONFIGURATION: MeasurementConfiguration = {
  system: "METRIC",
  millimetersPerInch: 25.4,
  kilogramsPerPound: 0.45359237,
  cubicMillimetersPerCubicInch: 16387.064,
  displayDecimals: 2
};

type SettingValue = { key: string; value: string };

export function measurementConfiguration(
  settings: SettingValue[] | undefined
): MeasurementConfiguration {
  const values = new Map(settings?.map((setting) => [setting.key, setting.value]));
  const positive = (key: string, fallback: number) => {
    const parsed = Number(values.get(key));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };
  const decimals = Number(values.get("MEASUREMENT_DISPLAY_DECIMALS"));
  return {
    system: values.get("MEASUREMENT_SYSTEM") === "IMPERIAL" ? "IMPERIAL" : "METRIC",
    millimetersPerInch: positive("LENGTH_MM_PER_INCH", 25.4),
    kilogramsPerPound: positive("MASS_KG_PER_POUND", 0.45359237),
    cubicMillimetersPerCubicInch: positive("VOLUME_MM3_PER_CUBIC_INCH", 16387.064),
    displayDecimals: Number.isInteger(decimals) && decimals >= 0 && decimals <= 6 ? decimals : 2
  };
}

function factor(kind: MeasurementKind, configuration: MeasurementConfiguration) {
  if (kind === "LENGTH") return configuration.millimetersPerInch;
  if (kind === "MASS") return configuration.kilogramsPerPound;
  return configuration.cubicMillimetersPerCubicInch;
}

export function fromMetric(value: number, kind: MeasurementKind,
  configuration: MeasurementConfiguration) {
  return configuration.system === "IMPERIAL" ? value / factor(kind, configuration) : value;
}

export function toMetric(value: number, kind: MeasurementKind,
  configuration: MeasurementConfiguration) {
  return configuration.system === "IMPERIAL" ? value * factor(kind, configuration) : value;
}

export function measurementUnit(kind: MeasurementKind,
  configuration: MeasurementConfiguration) {
  if (configuration.system === "METRIC")
    return kind === "LENGTH" ? "mm" : kind === "MASS" ? "kg" : "mm³";
  return kind === "LENGTH" ? "in" : kind === "MASS" ? "lb" : "in³";
}

export function measurementInputStep(kind: MeasurementKind,
  configuration: MeasurementConfiguration) {
  if (configuration.system === "IMPERIAL") return 0.01;
  if (kind === "MASS") return 0.001;
  return 1;
}

export function measurementInputValue(value: number, kind: MeasurementKind,
  configuration: MeasurementConfiguration) {
  const converted = fromMetric(value, kind, configuration);
  return String(Number(converted.toFixed(Math.max(
    configuration.displayDecimals, 6
  ))));
}

export function clampNonNegativeMeasurementInput(value: string) {
  if (value === "") return value;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return parsed < 0 ? "0" : value;
}

export function formatMeasurement(value: number, kind: MeasurementKind,
  configuration: MeasurementConfiguration, locale: string) {
  const converted = fromMetric(value, kind, configuration);
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: configuration.displayDecimals
  }).format(converted)} ${measurementUnit(kind, configuration)}`;
}
